import { MenuIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { Input } from "@xola/ui-kit";
import clsx from "clsx";
import { uniqueId } from "lodash";
import React, { useState } from "react";
import {
    DragDropContext,
    Draggable,
    DraggableProvidedDraggableProps,
    DraggableProvidedDragHandleProps,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
    DropResult,
} from "react-beautiful-dnd";
import { reorder } from "../../../utils/reorder";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface DragAndDropListProps {
    listType: string;
    items: string[];
    handleIncludedChange: (items: string[]) => void;
    handleNotIncludedChange: (items: string[]) => void;
}

export const DragAndDropList = ({
    listType,
    items: checklistItems,
    handleIncludedChange,
    handleNotIncludedChange,
}: DragAndDropListProps) => {
    const [items, setItems] = useState([...checklistItems]);

    const updateList = (items: any) => {
        setItems(items);
        if (listType === "included") {
            handleIncludedChange(items);
        } else {
            handleNotIncludedChange(items);
        }
    };

    const handleItemAdd = (item: string) => {
        items.push(item);
        updateList(items);
    };

    const handleItemDelete = (index: number) => {
        items.splice(index, 1);
        updateList(items);
    };

    const handleDragEnd = (result: DropResult) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const reOrderedItems = reorder(items, result.source.index, result.destination.index);

        updateList(reOrderedItems);
    };

    return (
        <DragDropContext onDragEnd={(result: DropResult) => handleDragEnd(result)}>
            <div>
                <Droppable key={listType} droppableId={listType}>
                    {(provided: DroppableProvided) => (
                        <div ref={provided.innerRef} className="h-full flex-1 rounded" {...provided.droppableProps}>
                            {items.map((item: string, index: number) => (
                                <Draggable key={uniqueId("id-")} draggableId={`id-${index}`} index={index}>
                                    {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                                        <DragableItem
                                            innerRef={innerRef}
                                            draggableProps={draggableProps}
                                            dragHandleProps={dragHandleProps}
                                            snapshot={snapshot}
                                            item={item}
                                            index={index}
                                            handleDelete={handleItemDelete}
                                        />
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
                <AddItem handleItemAdd={handleItemAdd} />
            </div>
        </DragDropContext>
    );
};

interface DragableItemProps {
    innerRef: (element?: HTMLElement | null | undefined) => any;
    draggableProps: DraggableProvidedDraggableProps;
    dragHandleProps?: DraggableProvidedDragHandleProps;
    snapshot: DraggableStateSnapshot;
    item: string;
    index: number;
    handleDelete: (index: number) => void;
}

const DragableItem = ({
    innerRef,
    draggableProps,
    dragHandleProps,
    snapshot,
    item,
    index,
    handleDelete,
}: DragableItemProps) => {
    const handleClick = () => {
        handleDelete(index);
    };

    return (
        <div
            ref={innerRef}
            {...draggableProps}
            {...dragHandleProps}
            className={clsx("bg-white py-2", { shadow: snapshot.isDragging })}
        >
            <div className="flex space-x-2">
                <div className="flex flex-grow items-center space-x-2 rounded border border-gray-light px-3">
                    <MenuIcon size="small" />
                    <div className="flex items-center ">
                        <NoTranslate>{item}</NoTranslate>
                    </div>
                </div>
                <SecondaryButton type="button" onClick={handleClick}>
                    <TrashIcon />
                </SecondaryButton>
            </div>
        </div>
    );
};

interface AddItemProps {
    handleItemAdd: (item: string) => void;
}

const AddItem = ({ handleItemAdd }: AddItemProps) => {
    const [item, setItem] = useState("");
    const handleClick = () => {
        if (item.trim().length > 0) {
            handleItemAdd(item);
            setItem("");
        }
    };

    const handleKeyPress = (e: any) => {
        if (e.key === "Enter") {
            handleClick();
        }
    };

    return (
        <div className="flex space-x-2 py-2">
            <Input
                value={item}
                placeholder="Add Item"
                onChange={(e: any) => setItem(e.target.value)}
                onKeyPress={handleKeyPress}
            />
            <SecondaryButton type="button" onClick={handleClick}>
                <PlusIcon />
            </SecondaryButton>
        </div>
    );
};
